import { useMsal } from '@azure/msal-react';
import { Button, Text, Checkbox, makeStyles, tokens } from '@fluentui/react-components';
import { useState } from 'react';
import { loginRequest } from '../config/authConfig';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1B2B4C 0%, #2B50AA 50%, #3B82F6 100%)',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '1 1 auto',
    padding: '24px 16px',
    '@media (max-width: 480px)': {
      padding: '16px 12px',
    },
  },
  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.97)',
    borderRadius: '16px',
    padding: '48px 40px',
    maxWidth: '440px',
    width: '100%',
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 8px 16px rgba(0, 0, 0, 0.1)',
    textAlign: 'center' as const,
    wordWrap: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    '@media (max-width: 480px)': {
      padding: '32px 20px',
      borderRadius: '12px',
    },
  },
  logoImage: {
    width: '80px',
    height: '80px',
    borderRadius: '16px',
    marginBottom: '16px',
    boxShadow: '0 4px 12px rgba(43, 80, 170, 0.2)',
  },
  title: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '4px',
    color: '#1B2B4C',
    letterSpacing: '-0.5px',
    '@media (max-width: 480px)': {
      fontSize: '22px',
    },
  },
  subtitle: {
    display: 'block',
    fontSize: '15px',
    marginBottom: '24px',
    color: tokens.colorNeutralForeground3,
    '@media (max-width: 480px)': {
      fontSize: '13px',
    },
  },
  description: {
    display: 'block',
    fontSize: '14px',
    marginBottom: '28px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.6',
    '@media (max-width: 480px)': {
      fontSize: '13px',
    },
  },
  consentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '4px',
    marginBottom: '24px',
    textAlign: 'left' as const,
  },
  consentText: {
    fontSize: '12px',
    lineHeight: '1.5',
    color: tokens.colorNeutralForeground3,
  },
  consentLink: {
    color: '#2B50AA',
    textDecoration: 'none',
    fontWeight: '500' as const,
    ':hover': {
      textDecoration: 'underline',
    },
  },
  roleHint: {
    display: 'block',
    fontSize: '12px',
    marginTop: '16px',
    color: tokens.colorNeutralForeground3,
  },
  poweredBy: {
    display: 'block',
    fontSize: '11px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
  },
  footer: {
    padding: '20px 16px',
    textAlign: 'center' as const,
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '8px',
    fontSize: '12px',
    lineHeight: '1.6',
    color: 'rgba(255, 255, 255, 0.7)',
  },
  footerLink: {
    color: 'rgba(255, 255, 255, 0.85)',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
      color: '#ffffff',
    },
  },
  footerDot: {
    fontSize: '10px',
    opacity: 0.5,
  },
});

export default function Login() {
  const { instance } = useMsal();
  const styles = useStyles();
  const [agreed, setAgreed] = useState(false);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.card}>
          <img src="/sherpai-logo.png" alt="Sherpai" className={styles.logoImage} />
          <Text className={styles.title}>Sherpai</Text>
          <Text className={styles.subtitle}>Your AI coach for getting the most from AI at work</Text>
          <Text className={styles.description}>
            Sign in with your organisation's Microsoft account to access your AI coaching dashboard,
            usage analytics, and team management.
          </Text>

          <div className={styles.consentRow}>
            <Checkbox
              checked={agreed}
              onChange={(_, data) => setAgreed(!!data.checked)}
            />
            <span className={styles.consentText}>
              I agree to the{' '}
              <a href="https://sherpai.component6.ai/terms" className={styles.consentLink} target="_blank" rel="noopener noreferrer">Terms of Service</a>
              ,{' '}
              <a href="https://sherpai.component6.ai/privacy" className={styles.consentLink} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              , and{' '}
              <a href="https://sherpai.component6.ai/dlp" className={styles.consentLink} target="_blank" rel="noopener noreferrer">Data Loss Prevention Policy</a>
              , including the use of AI as described in the{' '}
              <a href="https://sherpai.component6.ai/ai-disclosure" className={styles.consentLink} target="_blank" rel="noopener noreferrer">AI Usage Disclosure</a>.
            </span>
          </div>

          <Button
            appearance="primary"
            size="large"
            onClick={handleLogin}
            disabled={!agreed}
            style={{ width: '100%', height: '44px', fontWeight: 600 }}
          >
            Sign in with Microsoft
          </Button>

          <Text className={styles.roleHint}>
            Your IT administrator must grant access to this portal
          </Text>

          <Text className={styles.poweredBy}>
            A product of Component6 Pty Ltd (ABN 19 669 571 831)
          </Text>
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <a href="https://sherpai.component6.ai/privacy" className={styles.footerLink}>Privacy Policy</a>
          <span className={styles.footerDot}>·</span>
          <a href="https://sherpai.component6.ai/terms" className={styles.footerLink}>Terms of Service</a>
          <span className={styles.footerDot}>·</span>
          <a href="https://sherpai.component6.ai/dlp" className={styles.footerLink}>DLP Policy</a>
          <span className={styles.footerDot}>·</span>
          <a href="https://sherpai.component6.ai/ai-disclosure" className={styles.footerLink}>AI Disclosure</a>
          <span className={styles.footerDot}>·</span>
          <span>© 2026 Component6 Pty Ltd · Western Australia</span>
        </div>
      </footer>
    </div>
  );
}
